import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import DropDownScroll from '../../../../../components/DropDownScroll';
import DropDownSelect from '../../../../../components/DropDownSelect';
import CompanionAppUsageDateRange from '../../../../../enums/companionAppUsageDateRange';
import DatePicker from 'react-datepicker';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import {
  useGetCompanionAppUsageDistrictFiltersMutation,
  useGetCompanionAppUsageSchoolFiltersMutation,
  useGetCompanionAppUsageTeacherFiltersMutation,
  useGetCompanionAppUsageStudentFiltersMutation
} from '../../../../../features/authentication/companionAppUsageApiSlice';
import {
  useGetCompanionAppUsageFiltersMutation,
  useUpdateCompanionAppUsageFiltersMutation
} from '../../../../../features/authentication/persistApiSlice';
import CustomToast from '../../../../../components/Toast';
import '../../../styles/reports.css';
import Skeleton from 'react-loading-skeleton';

const DATE_RANGE_OPTIONS = [
  { id: CompanionAppUsageDateRange.FromStartOfCurrentSchoolYear, name: 'From Start of Current School Year' },
  { id: CompanionAppUsageDateRange.Last7Days, name: 'Last 7 Days' },
  { id: CompanionAppUsageDateRange.Last30Days, name: 'Last 30 Days' },
  { id: CompanionAppUsageDateRange.Last90Days, name: 'Last 90 Days' },
  { id: CompanionAppUsageDateRange.CustomRange, name: 'Custom Date Range' }
];

const DEFAULT_DISTRICT_OPTIONS = [{ id: -1, name: 'All Districts' }];
const DEFAULT_SCHOOL_OPTIONS = [{ id: -1, name: 'All Schools' }];
const DEFAULT_TEACHER_OPTIONS = [{ id: -1, name: 'All Teachers' }];
const DEFAULT_STUDENT_OPTIONS = [{ id: -1, name: 'All Students' }];

const getPageRawCount = (response) => (Array.isArray(response) ? response.length : (response?.data?.length ?? 0));

const getHasMore = (response, skipValue, takeSize) => {
  if (Array.isArray(response)) {
    return response.length >= takeSize;
  }

  const total = response?.total;
  if (typeof total === 'number') {
    return (skipValue + takeSize) < total;
  }

  return getPageRawCount(response) >= takeSize;
};

const AppUsageFilter = ({ handleCreateReport }) => {
  const [getDistrictFilters, { isLoading: isDistrictFiltersLoading }] = useGetCompanionAppUsageDistrictFiltersMutation();
  const [getSchoolFilters, { isLoading: isSchoolFiltersLoading }] = useGetCompanionAppUsageSchoolFiltersMutation();
  const [getTeacherFilters, { isLoading: isTeacherFiltersLoading }] = useGetCompanionAppUsageTeacherFiltersMutation();
  const [getStudentFilters, { isLoading: isStudentFiltersLoading }] = useGetCompanionAppUsageStudentFiltersMutation();
  const [getCompanionAppUsageFilters] = useGetCompanionAppUsageFiltersMutation();
  const [updateCompanionAppUsageFilters] = useUpdateCompanionAppUsageFiltersMutation();

  const [districtOptions, setDistrictOptions] = useState(DEFAULT_DISTRICT_OPTIONS);
  const [schoolOptions, setSchoolOptions] = useState(DEFAULT_SCHOOL_OPTIONS);
  const [teacherOptions, setTeacherOptions] = useState(DEFAULT_TEACHER_OPTIONS);
  const [studentOptions, setStudentOptions] = useState(DEFAULT_STUDENT_OPTIONS);

  const [dateRangeId, setDateRangeId] = useState(DATE_RANGE_OPTIONS[0].id);
  const [dateRangeName, setDateRangeName] = useState(DATE_RANGE_OPTIONS[0].name);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [districtId, setDistrictId] = useState(DEFAULT_DISTRICT_OPTIONS[0].id);
  const [districtName, setDistrictName] = useState(DEFAULT_DISTRICT_OPTIONS[0].name);
  const [schoolId, setSchoolId] = useState(DEFAULT_SCHOOL_OPTIONS[0].id);
  const [schoolName, setSchoolName] = useState(DEFAULT_SCHOOL_OPTIONS[0].name);
  const [teacherId, setTeacherId] = useState(DEFAULT_TEACHER_OPTIONS[0].id);
  const [teacherName, setTeacherName] = useState(DEFAULT_TEACHER_OPTIONS[0].name);
  const [studentId, setStudentId] = useState(DEFAULT_STUDENT_OPTIONS[0].id);
  const [studentName, setStudentName] = useState(DEFAULT_STUDENT_OPTIONS[0].name);
  const [enableYearComparison, setEnableYearComparison] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [districtSearchString, setDistrictSearchString] = useState('');
  const [schoolSearchString, setSchoolSearchString] = useState('');
  const [teacherSearchString, setTeacherSearchString] = useState('');
  const [studentSearchString, setStudentSearchString] = useState('');
  const [debouncedDistrictSearchString, setDebouncedDistrictSearchString] = useState('');
  const [debouncedSchoolSearchString, setDebouncedSchoolSearchString] = useState('');
  const [debouncedTeacherSearchString, setDebouncedTeacherSearchString] = useState('');
  const [debouncedStudentSearchString, setDebouncedStudentSearchString] = useState('');
  const [schoolDataLoading, setSchoolDataLoading] = useState(false);
  const [teacherDataLoading, setTeacherDataLoading] = useState(false);
  const [studentDataLoading, setStudentDataLoading] = useState(false);
  const [isResettingFilters, setIsResettingFilters] = useState(false);
  const [districtSkip, setDistrictSkip] = useState(0);
  const [schoolSkip, setSchoolSkip] = useState(0);
  const [teacherSkip, setTeacherSkip] = useState(0);
  const [studentSkip, setStudentSkip] = useState(0);
  const [districtDropdownKey, setDistrictDropdownKey] = useState(0);
  const [schoolDropdownKey, setSchoolDropdownKey] = useState(0);
  const [teacherDropdownKey, setTeacherDropdownKey] = useState(0);
  const [studentDropdownKey, setStudentDropdownKey] = useState(0);
  const [districtHasMore, setDistrictHasMore] = useState(true);
  const [schoolHasMore, setSchoolHasMore] = useState(true);
  const [teacherHasMore, setTeacherHasMore] = useState(true);
  const [studentHasMore, setStudentHasMore] = useState(true);

  const takeSize = 30;

  const normalizeOptions = (response, allLabel) => {
    const payload = Array.isArray(response) ? response : (response?.data || []);
    const mapped = payload
      .map((item) => {
        const id = item.id ?? item.districtId ?? item.schoolId ?? item.teacherId ?? item.studentId ?? item.userId;
        const name = item.name
          ?? item.districtName
          ?? item.schoolName
          ?? item.teacherName
          ?? item.studentName
          ?? `${item.fName || item.firstName || ''} ${item.lName || item.lastName || ''}`.trim();

        if (id === undefined || name === undefined || name === '') {
          return null;
        }

        return { id: Number(id), name };
      })
      .filter(Boolean);

    return [{ id: -1, name: allLabel }, ...mapped];
  };

  const getPersistValue = (model, key, fallback) => {
    if (model == null) return fallback;
    return model[key] !== undefined && model[key] !== null ? model[key] : fallback;
  };

  const asValidDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const pickSelectedOption = (options, selectedId, fallbackName) => {
    const selected = options.find((item) => item.id === selectedId);
    return selected ? selected.name : fallbackName;
  };

  const loadDistrictOptions = useCallback(async (searchString = '', skipValue = 0, append = false) => {
    const response = await getDistrictFilters({ skip: skipValue, take: takeSize, searchString }).unwrap();
    const options = normalizeOptions(response, 'All Districts');
    setDistrictHasMore(getHasMore(response, skipValue, takeSize));
    if (append) {
      setDistrictOptions((prev) => [...prev, ...options.slice(1)]);
    } else {
      setDistrictOptions(options);
    }
    return options;
  }, [getDistrictFilters, takeSize]);

  const loadSchoolOptions = useCallback(async (selectedDistrictId = -1, searchString = '', skipValue = 0, append = false) => {
    const response = await getSchoolFilters({
      skip: skipValue,
      take: takeSize,
      districtId: selectedDistrictId,
      searchString
    }).unwrap();
    const options = normalizeOptions(response, 'All Schools');
    setSchoolHasMore(getHasMore(response, skipValue, takeSize));
    if (append) {
      setSchoolOptions((prev) => [...prev, ...options.slice(1)]);
    } else {
      setSchoolOptions(options);
    }
    return options;
  }, [getSchoolFilters, takeSize]);

  const loadTeacherOptions = useCallback(async (selectedDistrictId = -1, selectedSchoolId = -1, searchString = '', skipValue = 0, append = false) => {
    const response = await getTeacherFilters({
      skip: skipValue,
      take: takeSize,
      districtId: selectedDistrictId,
      schoolId: selectedSchoolId,
      searchString
    }).unwrap();
    const options = normalizeOptions(response, 'All Teachers');
    setTeacherHasMore(getHasMore(response, skipValue, takeSize));
    if (append) {
      setTeacherOptions((prev) => [...prev, ...options.slice(1)]);
    } else {
      setTeacherOptions(options);
    }
    return options;
  }, [getTeacherFilters, takeSize]);

  const loadStudentOptions = useCallback(async (selectedDistrictId = -1, selectedSchoolId = -1, selectedTeacherId = -1, searchString = '', skipValue = 0, append = false) => {
    const response = await getStudentFilters({
      skip: skipValue,
      take: takeSize,
      districtId: selectedDistrictId,
      schoolId: selectedSchoolId,
      teacherId: selectedTeacherId,
      searchString
    }).unwrap();
    const options = normalizeOptions(response, 'All Students');
    setStudentHasMore(getHasMore(response, skipValue, takeSize));
    if (append) {
      setStudentOptions((prev) => [...prev, ...options.slice(1)]);
    } else {
      setStudentOptions(options);
    }
    return options;
  }, [getStudentFilters, takeSize]);

  const loadDefaultFilterOptions = useCallback(async () => {
    await Promise.all([
      loadDistrictOptions('', 0, false),
      loadSchoolOptions(-1, '', 0, false),
      loadTeacherOptions(-1, -1, '', 0, false),
      loadStudentOptions(-1, -1, -1, '', 0, false)
    ]);
  }, [loadDistrictOptions, loadSchoolOptions, loadTeacherOptions, loadStudentOptions]);

  useEffect(() => {
    const loadInitialOptions = async () => {
      try {
        setIsLoading(true);
        const persistedFilter = await getCompanionAppUsageFilters().unwrap();

        const persistedDateRange = Number(getPersistValue(persistedFilter, 'dateRange', CompanionAppUsageDateRange.FromStartOfCurrentSchoolYear));
        const persistedDistrictId = Number(getPersistValue(persistedFilter, 'districtId', -1));
        const persistedDistrictName = getPersistValue(persistedFilter, 'districtName', 'All Districts');
        const persistedSchoolId = Number(getPersistValue(persistedFilter, 'schoolId', -1));
        const persistedSchoolName = getPersistValue(persistedFilter, 'schoolName', 'All Schools');
        const persistedTeacherId = Number(getPersistValue(persistedFilter, 'teacherId', -1));
        const persistedTeacherName = getPersistValue(persistedFilter, 'teacherName', 'All Teachers');
        const persistedStudentId = Number(getPersistValue(persistedFilter, 'studentId', -1));
        const persistedStudentName = getPersistValue(persistedFilter, 'studentName', 'All Students');
        const persistedYearOverYear = Boolean(getPersistValue(persistedFilter, 'includeYearOverYearComparison', true));
        const persistedStartDate = asValidDate(getPersistValue(persistedFilter, 'startDateUtc', null));
        const persistedEndDate = asValidDate(getPersistValue(persistedFilter, 'endDateUtc', null));

        const districtList = await loadDistrictOptions('', 0, false);
        const schoolList = await loadSchoolOptions(persistedDistrictId, '', 0, false);
        const teacherList = await loadTeacherOptions(persistedDistrictId, persistedSchoolId, '', 0, false);
        const studentList = await loadStudentOptions(persistedDistrictId, persistedSchoolId, persistedTeacherId, '', 0, false);

        setDistrictOptions(districtList);
        setSchoolOptions(schoolList);
        setTeacherOptions(teacherList);
        setStudentOptions(studentList);

        const selectedDateRangeOption = DATE_RANGE_OPTIONS.find((item) => item.id === persistedDateRange) || DATE_RANGE_OPTIONS[0];
        setDateRangeId(selectedDateRangeOption.id);
        setDateRangeName(selectedDateRangeOption.name);
        setStartDate(persistedStartDate);
        setEndDate(persistedEndDate);
        setEnableYearComparison(persistedYearOverYear);

        setDistrictId(persistedDistrictId);
        setDistrictName(persistedDistrictId > 0 ? persistedDistrictName : 'All Districts');
        setSchoolId(persistedSchoolId);
        setSchoolName(persistedSchoolId > 0 ? persistedSchoolName : 'All Schools');
        setTeacherId(persistedTeacherId);
        setTeacherName(persistedTeacherId > 0 ? persistedTeacherName : 'All Teachers');
        setStudentId(persistedStudentId);
        setStudentName(persistedStudentId > 0 ? persistedStudentName : 'All Students');
      } catch (error) {
        CustomToast(error?.data?.error || 'Unable to load filter values.', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialOptions();
  }, [
    getCompanionAppUsageFilters,
    loadDistrictOptions,
    loadSchoolOptions,
    loadTeacherOptions,
    loadStudentOptions
  ]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDistrictSearchString(districtSearchString);
    }, 500);
    return () => clearTimeout(handler);
  }, [districtSearchString]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSchoolSearchString(schoolSearchString);
    }, 500);
    return () => clearTimeout(handler);
  }, [schoolSearchString]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedTeacherSearchString(teacherSearchString);
    }, 500);
    return () => clearTimeout(handler);
  }, [teacherSearchString]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedStudentSearchString(studentSearchString);
    }, 500);
    return () => clearTimeout(handler);
  }, [studentSearchString]);

  const hasMountedDistrictSearch = useRef(false);
  const hasMountedSchoolSearch = useRef(false);
  const hasMountedTeacherSearch = useRef(false);
  const hasMountedStudentSearch = useRef(false);
  const districtIdRef = useRef(districtId);
  const schoolIdRef = useRef(schoolId);
  const teacherIdRef = useRef(teacherId);

  useEffect(() => { districtIdRef.current = districtId; }, [districtId]);
  useEffect(() => { schoolIdRef.current = schoolId; }, [schoolId]);
  useEffect(() => { teacherIdRef.current = teacherId; }, [teacherId]);

  const suppressDistrictSearchEffect = useRef(false);
  const suppressSchoolSearchEffect = useRef(false);
  const suppressTeacherSearchEffect = useRef(false);
  const suppressStudentSearchEffect = useRef(false);

  useEffect(() => {
    if (!hasMountedDistrictSearch.current) {
      hasMountedDistrictSearch.current = true;
      return;
    }
    if (suppressDistrictSearchEffect.current) {
      suppressDistrictSearchEffect.current = false;
      return;
    }
    setDistrictSkip(0);
    loadDistrictOptions(debouncedDistrictSearchString, 0, false);
  }, [debouncedDistrictSearchString, loadDistrictOptions]);

  useEffect(() => {
    if (!hasMountedSchoolSearch.current) {
      hasMountedSchoolSearch.current = true;
      return;
    }
    if (suppressSchoolSearchEffect.current) {
      suppressSchoolSearchEffect.current = false;
      return;
    }
    setSchoolSkip(0);
    loadSchoolOptions(districtIdRef.current, debouncedSchoolSearchString, 0, false);
  }, [debouncedSchoolSearchString, loadSchoolOptions]);

  useEffect(() => {
    if (!hasMountedTeacherSearch.current) {
      hasMountedTeacherSearch.current = true;
      return;
    }
    if (suppressTeacherSearchEffect.current) {
      suppressTeacherSearchEffect.current = false;
      return;
    }
    setTeacherSkip(0);
    loadTeacherOptions(districtIdRef.current, schoolIdRef.current, debouncedTeacherSearchString, 0, false);
  }, [debouncedTeacherSearchString, loadTeacherOptions]);

  useEffect(() => {
    if (!hasMountedStudentSearch.current) {
      hasMountedStudentSearch.current = true;
      return;
    }
    if (suppressStudentSearchEffect.current) {
      suppressStudentSearchEffect.current = false;
      return;
    }
    setStudentSkip(0);
    loadStudentOptions(districtIdRef.current, schoolIdRef.current, teacherIdRef.current, debouncedStudentSearchString, 0, false);
  }, [debouncedStudentSearchString, loadStudentOptions]);

  const resetTeacherAndStudentFilters = () => {
    setTeacherId(-1);
    setTeacherName('All Teachers');
    setStudentId(-1);
    setStudentName('All Students');
    setTeacherSkip(0);
    setStudentSkip(0);
  };
  const savePersistedFilter = async () => {
    const payload = {
      dateRange: dateRangeId,
      startDateUtc: startDate ? startDate.toISOString() : null,
      endDateUtc: endDate ? endDate.toISOString() : null,
      districtId,
      districtName,
      schoolId,
      schoolName,
      teacherId,
      teacherName,
      studentId,
      studentName,
      includeYearOverYearComparison: enableYearComparison
    };

    await updateCompanionAppUsageFilters(payload).unwrap();
  };

  const handleCreateReportClick = async () => {
    try {
      await savePersistedFilter();
      handleCreateReport();
    } catch (error) {
      CustomToast(error?.data?.error || 'Unable to save filter values.', 'error');
    }
  };

  const handleDistrictLoadMore = useCallback(() => {
    if (!districtHasMore) return;
    const nextSkip = districtSkip + takeSize;
    loadDistrictOptions(districtSearchString, nextSkip, true).finally(() => setDistrictSkip(nextSkip));
  }, [districtHasMore, districtSkip, districtSearchString, loadDistrictOptions, takeSize]);

  const handleSchoolLoadMore = useCallback(() => {
    if (!schoolHasMore) return;
    const nextSkip = schoolSkip + takeSize;
    loadSchoolOptions(districtId, schoolSearchString, nextSkip, true).finally(() => setSchoolSkip(nextSkip));
  }, [schoolHasMore, districtId, schoolSkip, schoolSearchString, loadSchoolOptions, takeSize]);

  const handleTeacherLoadMore = useCallback(() => {
    if (!teacherHasMore) return;
    const nextSkip = teacherSkip + takeSize;
    loadTeacherOptions(districtId, schoolId, teacherSearchString, nextSkip, true).finally(() => setTeacherSkip(nextSkip));
  }, [teacherHasMore, districtId, schoolId, teacherSkip, teacherSearchString, loadTeacherOptions, takeSize]);

  const handleStudentLoadMore = useCallback(() => {
    if (!studentHasMore) return;
    const nextSkip = studentSkip + takeSize;
    loadStudentOptions(districtId, schoolId, teacherId, studentSearchString, nextSkip, true).finally(() => setStudentSkip(nextSkip));
  }, [studentHasMore, districtId, schoolId, teacherId, studentSkip, studentSearchString, loadStudentOptions, takeSize]);

  const handleDistrictIdChange = async (newDistrictId) => {
    setDistrictId(newDistrictId);
    setDistrictName(pickSelectedOption(districtOptions, newDistrictId, 'All Districts'));

    setSchoolId(-1);
    setSchoolName('All Schools');
    resetTeacherAndStudentFilters();
    setDistrictSearchString('');
    setSchoolSkip(0);
    setDistrictDropdownKey((prev) => prev + 1);
    setSchoolDropdownKey((prev) => prev + 1);
    setTeacherDropdownKey((prev) => prev + 1);
    setStudentDropdownKey((prev) => prev + 1);

    try {
      setSchoolDataLoading(true);
      setTeacherDataLoading(true);
      setStudentDataLoading(true);
      await loadSchoolOptions(newDistrictId, '', 0, false);
      await loadTeacherOptions(newDistrictId, -1, '', 0, false);
      await loadStudentOptions(newDistrictId, -1, -1, '', 0, false);
    } catch (error) {
      CustomToast(error?.data?.error || 'Unable to update filters.', 'error');
    } finally {
      setSchoolDataLoading(false);
      setTeacherDataLoading(false);
      setStudentDataLoading(false);
    }
  };

  const handleSchoolIdChange = async (newSchoolId) => {
    setSchoolId(newSchoolId);
    setSchoolName(pickSelectedOption(schoolOptions, newSchoolId, 'All Schools'));

    resetTeacherAndStudentFilters();
    setSchoolSearchString('');
    setSchoolDropdownKey((prev) => prev + 1);
    setTeacherDropdownKey((prev) => prev + 1);
    setStudentDropdownKey((prev) => prev + 1);

    try {
      setTeacherDataLoading(true);
      setStudentDataLoading(true);
      await loadTeacherOptions(districtId, newSchoolId, '', 0, false);
      await loadStudentOptions(districtId, newSchoolId, -1, '', 0, false);
    } catch (error) {
      CustomToast(error?.data?.error || 'Unable to update filters.', 'error');
    } finally {
      setTeacherDataLoading(false);
      setStudentDataLoading(false);
    }
  };

  const handleTeacherIdChange = async (newTeacherId) => {
    setTeacherId(newTeacherId);
    setTeacherName(pickSelectedOption(teacherOptions, newTeacherId, 'All Teachers'));

    setStudentId(-1);
    setStudentName('All Students');
    setTeacherSearchString('');
    setStudentSkip(0);
    setTeacherDropdownKey((prev) => prev + 1);
    setStudentDropdownKey((prev) => prev + 1);

    try {
      setStudentDataLoading(true);
      await loadStudentOptions(districtId, schoolId, newTeacherId, '', 0, false);
    } catch (error) {
      CustomToast(error?.data?.error || 'Unable to update filters.', 'error');
    } finally {
      setStudentDataLoading(false);
    }
  };

  const handleStudentIdChange = (newStudentId) => {
    setStudentId(newStudentId);
    setStudentName(pickSelectedOption(studentOptions, newStudentId, 'All Students'));
    setStudentSearchString('');
    setStudentDropdownKey((prev) => prev + 1);
  };

  const handleResetFilters = async () => {
    setIsResettingFilters(true);
    setDateRangeId(DATE_RANGE_OPTIONS[0].id);
    setDateRangeName(DATE_RANGE_OPTIONS[0].name);
    setStartDate(null);
    setEndDate(null);
    setDistrictId(-1);
    setDistrictName('All Districts');
    setSchoolId(-1);
    setSchoolName('All Schools');
    resetTeacherAndStudentFilters();
    setEnableYearComparison(true);

    setDistrictSearchString('');
    setSchoolSearchString('');
    setTeacherSearchString('');
    setStudentSearchString('');
    if (debouncedDistrictSearchString !== '') suppressDistrictSearchEffect.current = true;
    if (debouncedSchoolSearchString !== '') suppressSchoolSearchEffect.current = true;
    if (debouncedTeacherSearchString !== '') suppressTeacherSearchEffect.current = true;
    if (debouncedStudentSearchString !== '') suppressStudentSearchEffect.current = true;
    setDebouncedDistrictSearchString('');
    setDebouncedSchoolSearchString('');
    setDebouncedTeacherSearchString('');
    setDebouncedStudentSearchString('');

    setDistrictSkip(0);
    setSchoolSkip(0);
    setDistrictDropdownKey((prev) => prev + 1);
    setSchoolDropdownKey((prev) => prev + 1);
    setTeacherDropdownKey((prev) => prev + 1);
    setStudentDropdownKey((prev) => prev + 1);

    try {
      await loadDefaultFilterOptions();
    } catch (error) {
      CustomToast(error?.data?.error || 'Unable to reset filter values.', 'error');
    } finally {
      setIsResettingFilters(false);
    }
  };

  const handleDateRangeChange = (selectedIdValue, selectedNameValue) => {
    const numericId = Number(selectedIdValue);
    setDateRangeId(numericId);
    setDateRangeName(selectedNameValue);
  };

  return (
    <div className="white-card">
      <div className="align-items-center d-flex justify-content-between mb-4">
        <div>
          <h1 className="mb-1">Companion App Usage Report</h1>
        </div>
        <button onClick={() => window.history.back()} className="btn btn-castleWhite report-btn">
          Back
        </button>
      </div>
      <p>
        The Companion App Usage Report summarizes student activity and engagement metrics on the companion app.
      </p>
      {isLoading ? (
        <SkeletonGrid
          rowCount={2}
          colCount={3}
          className={'mt-5 p-2'}
        />
      ) : (
        <>
          <Row className="mt-4 g-3 align-items-end">
            <Col lg={4} md={6}>
              <label className="mb-2">Date Range</label>
              <DropDownSelect
                value={DATE_RANGE_OPTIONS}
                selectedId={dateRangeId}
                setSelectedId={handleDateRangeChange}
                dropdownId="companion-date-range"
                selectedName={dateRangeName}
                setSelectedName={setDateRangeName}
                showSearch={false}
              />
            </Col>
            <Col lg={4} md={6}>
              <div className="form-check">
                <div>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="yearOverYearToggle"
                    checked={enableYearComparison}
                    onChange={(e) => setEnableYearComparison(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="yearOverYearToggle">
                    Year-over-Year Comparison
                  </label>
                </div>
              </div>
            </Col>
            <Col lg={4} md={12} className="d-flex justify-content-lg-end align-items-end">
              <button type="button" className="btn-link primary-link px-0" onClick={handleResetFilters}>
                Reset Filters
              </button>
            </Col>
          </Row>

          {dateRangeId == CompanionAppUsageDateRange.CustomRange && (
            <Row className="mt-1 g-3 align-items-end">
              <Col lg={8} md={12}>
                <div className="d-flex flex-column flex-md-row gap-3">
                  <div className="w-100">
                    <label className="mb-2">Start Date</label>
                    <div className="date-picker-input p-0">
                      <DatePicker
                        selected={startDate}
                        onChange={(e) => setStartDate(e)}
                        dateFormat={'MM-dd-yyyy'}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="w-100">
                    <label className="mb-2">End Date</label>
                    <div className="date-picker-input p-0">
                      <DatePicker
                        selected={endDate}
                        onChange={(e) => setEndDate(e)}
                        dateFormat={'MM-dd-yyyy'}
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Row className="mt-1 g-3 align-items-end">
            <Col lg={3} md={6}>
              <label className="mb-2">District</label>
              {isResettingFilters ? (
                <Skeleton className='h-47' />
              ) : (
                <DropDownScroll
                  key={districtDropdownKey}
                  value={districtOptions}
                  selectedId={districtId}
                  setSelectedId={handleDistrictIdChange}
                  dropdownId="companion-district"
                  selectedName={districtName}
                  setSelectedName={setDistrictName}
                  loadMoreData={handleDistrictLoadMore}
                  isLoading={isDistrictFiltersLoading}
                  setSearchString={setDistrictSearchString}
                />
              )}
            </Col>
            <Col lg={3} md={6}>
              <label className="mb-2">School</label>
              {schoolDataLoading || isResettingFilters ? (
                <Skeleton className='h-47' />
              ) : (
                <DropDownScroll
                  key={schoolDropdownKey}
                  value={schoolOptions}
                  selectedId={schoolId}
                  setSelectedId={handleSchoolIdChange}
                  dropdownId="companion-school"
                  selectedName={schoolName}
                  setSelectedName={setSchoolName}
                  loadMoreData={handleSchoolLoadMore}
                  isLoading={isSchoolFiltersLoading}
                  setSearchString={setSchoolSearchString}
                />
              )}
            </Col>
            <Col lg={3} md={6}>
              <label className="mb-2">Teacher</label>
              {teacherDataLoading || isResettingFilters ? (
                <Skeleton className='h-47' />
              ) : (
                <DropDownScroll
                  key={teacherDropdownKey}
                  value={teacherOptions}
                  selectedId={teacherId}
                  setSelectedId={handleTeacherIdChange}
                  dropdownId="companion-teacher"
                  selectedName={teacherName}
                  setSelectedName={setTeacherName}
                  loadMoreData={handleTeacherLoadMore}
                  isLoading={isTeacherFiltersLoading}
                  setSearchString={setTeacherSearchString}
                />
              )}
            </Col>
            <Col lg={3} md={6}>
              <label className="mb-2">Student</label>
              {studentDataLoading || isResettingFilters ? (
                <Skeleton className='h-47' />
              ) : (
                <DropDownScroll
                  key={studentDropdownKey}
                  value={studentOptions}
                  selectedId={studentId}
                  setSelectedId={handleStudentIdChange}
                  dropdownId="companion-student"
                  selectedName={studentName}
                  setSelectedName={setStudentName}
                  loadMoreData={handleStudentLoadMore}
                  isLoading={isStudentFiltersLoading}
                  setSearchString={setStudentSearchString}
                />
              )}
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-end mt-4">
            <div>
              <button
                type="button"
                className="btn btn-castleRed px-4"
                onClick={handleCreateReportClick}
                disabled={schoolDataLoading || teacherDataLoading || studentDataLoading || studentOptions.length <= 1}
              >
                Create Report
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

AppUsageFilter.propTypes = {
  handleCreateReport: PropTypes.func
};

export default AppUsageFilter;